import { useRuntimeConfig } from '#imports'
import { type Ref, computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { useGalleryVideoSource } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/composables/useGalleryVideoSource'

import translations from '../GalleryNew.translations'
import type {
  GalleryImage,
  GalleryImageExtraItem,
  ReviewWithCount,
} from '../GalleryNew.types'

/**
 * Make sure the ideal index is different
 * in each extra item type.
 *
 * `idealIndex` is the 0-indexed position of
 * the extra item in the carousel.
 */
export const EXTRA_ITEMS_CONFIGURATION = Object.freeze({
  review: {
    idealIndex: 4,
  },
  video: {
    idealIndex: 3,
  },
  jpRefurbishmentInfographic: {
    idealIndex: 1,
  },
})

/**
 * Helper function to encapsulate the logic for extra items in the gallery.
 * Each item type will have a different logic: different source and positionning logic.
 */
export function useExtraItems({
  baseCarouselImages,
  review,
  videoParams,
}: {
  baseCarouselImages: Ref<GalleryImage[]>
  review?: ReviewWithCount | null
  videoParams?: { brand?: string; categoryId?: string }
}) {
  const config = useRuntimeConfig()
  const experiments = useExperiments()
  const i18n = useI18n()
  const { market } = useMarketplace()

  // Resolves YouTube vs self-hosted video + the matching poster/title (the
  // poster is the thumbnail shown in the carousel cell).
  const videoSource = useGalleryVideoSource(videoParams)

  // Pre-compute feature flag once (available at SSR)
  const isVideosInCarouselEnabled =
    config.public.FF_KILL_VIDEOS_IN_NEW_CAROUSEL === false

  // Pre-compute review validity once
  const hasValidReview =
    review &&
    review.id &&
    review.pictures &&
    Array.isArray(review.pictures) &&
    review.pictures.length > 0

  const isJpRefurbishmentInfographicEnabled =
    market.countryCode === 'JP' &&
    experiments['experiment.jpRefurbishmentInfographic'] ===
      'withJpRefurbishmentInfographic'

  const isExtra = (item: GalleryImage) => {
    return 'isExtra' in item && item.isExtra
  }

  function injectExtraItemsAtPosition(
    items: Ref<GalleryImage[]>,
  ): GalleryImage[] {
    if (!items.value || !Array.isArray(items.value)) {
      return []
    }

    const result = [...items.value]
    const extraItemsToAdd: GalleryImageExtraItem[] = []

    // Build extra items array directly (no computed chain)
    if (isVideosInCarouselEnabled) {
      extraItemsToAdd.push({
        id: `extra:video-${videoSource.poster}`,
        src: videoSource.poster,
        alt: videoSource.title,
        isExtra: true as const,
        type: 'video' as const,
        desktopOrder: 0,
      } satisfies GalleryImageExtraItem)
    }

    if (hasValidReview) {
      extraItemsToAdd.push({
        src: review.pictures[0],
        alt: review.product?.title || '',
        id: `extra:review-${review.id}`,
        isExtra: true as const,
        type: 'review' as const,
        desktopOrder: 0,
      } satisfies GalleryImageExtraItem)
    }

    // As this extra going to be before others in final images list, it should be inserted last.
    // So when it's inserted, the other extras also shift their position.
    if (isJpRefurbishmentInfographicEnabled) {
      extraItemsToAdd.push({
        id: 'extra:refurbishment-infographic',
        src: '/img/product/jp/refurbishment-infographic.svg',
        alt: i18n(translations.jpRefurbishmentInfographicAlt),
        isExtra: true as const,
        type: 'jpRefurbishmentInfographic' as const,
        desktopOrder: 0,
      } satisfies GalleryImageExtraItem)
    }

    if (extraItemsToAdd.length === 0) {
      return items.value
    }

    extraItemsToAdd.forEach((item) => {
      const itemType = item.type
      const itemsConfig = itemType
        ? EXTRA_ITEMS_CONFIGURATION[itemType]
        : undefined

      // If configuration exists, use the ideal index
      // Otherwise, append to the end as a fallback
      const idealIndex = itemsConfig
        ? Math.min(
            itemsConfig.idealIndex || Number.MAX_SAFE_INTEGER,
            result.length,
          )
        : result.length

      result.splice(idealIndex, 0, item)
    })

    return result
  }

  /**
   * This function is used to get the desktop flex-order of an item based
   * its type and the presence of videos and reviews.
   *
   * To prevent hydration mismatches, we calculate the order based on the
   * enabled features rather than the actual presence of items in the list.
   */
  const getDesktopOrder = (item: GalleryImage, index: number): number => {
    // Define order rules as a map for better readability
    // These rules match the original CSS md:order-* classes exactly
    const orderRules = [
      // md:order-1: extras
      // Ordering between extras depends on how they appear in the "images with extras".
      { condition: isExtra(item), order: 1 },

      // md:order-2: product images, except the 1st one (index=0) that will use 'initial'.
      // Ordering between them depends on their initial order in product images.
      { condition: index >= 1, order: 2 },
    ]

    // Find the first matching rule
    const matchingRule = orderRules.find(({ condition }) => condition)

    // fallback to current position
    return matchingRule?.order ?? index
  }

  const imagesListWithExtraItems = computed(() => {
    return injectExtraItemsAtPosition(baseCarouselImages).map(
      (item, index) => ({
        ...item,
        desktopOrder: getDesktopOrder(item, index),
      }),
    )
  })

  const extraItemsIndexes = computed(() => {
    return imagesListWithExtraItems.value.length >
      baseCarouselImages.value.length
      ? imagesListWithExtraItems.value.reduce((acc, image, index) => {
          if (isExtra(image)) {
            acc.push(index)
          }

          return acc
        }, [] as number[])
      : []
  })

  return {
    extraItemsIndexes,
    imagesListWithExtraItems,
    injectExtraItemsAtPosition,
    videoSource,
  }
}
